import { SCENARIO_COLORS } from '../config/brandColors';

/**
 * Mock pivot-points dataset for the explicit demo mode (?demo=true) only — never
 * a fallback for a missing instance or a failed fetch (#1413). Extracted
 * verbatim from PivotPointsScreen so the screen stays focused on view logic.
 *
 * Returns { scenarios, nodes } in the same shape PivotPointsView expects.
 */
export function generateDemoData() {
  const demoScenarios = [
    {
      id: 'demokratia',
      title: 'Demokratia',
      icon: '🏛️',
      color: SCENARIO_COLORS.Demokratia,
      variables: [
        { id: 'dem-1', title: 'Talous ja geopolitiikka' },
        { id: 'dem-2', title: 'Teknologinen muutos' },
        { id: 'dem-3', title: 'Muuttoliike' },
        { id: 'dem-4', title: 'Päätöksenteon rakenteet' },
        { id: 'dem-5', title: 'Ideologinen kenttä' },
        { id: 'dem-6', title: 'Ilmastopolitiikka' }
      ]
    },
    {
      id: 'ilmasto',
      title: 'Ilmasto',
      icon: '🌍',
      color: SCENARIO_COLORS.Ilmasto,
      variables: [
        { id: 'ilm-1', title: 'Keskilämpötila' },
        { id: 'ilm-2', title: 'Veden kierrot' },
        { id: 'ilm-3', title: 'Sään ääri-ilmiöt' },
        { id: 'ilm-4', title: 'Biologiset vaikutukset' },
        { id: 'ilm-5', title: 'Globaalit muutokset' },
        { id: 'ilm-6', title: 'Heijastevaikutukset' }
      ]
    },
    {
      id: 'teknologia',
      title: 'Teknologia',
      icon: '💻',
      color: SCENARIO_COLORS.Teknologia,
      variables: [
        { id: 'tek-1', title: 'Tekoälyn kehitys' },
        { id: 'tek-2', title: 'Automaation taso' },
        { id: 'tek-3', title: 'Digitalisaatio' },
        { id: 'tek-4', title: 'Kyberturvallisuus' },
        { id: 'tek-5', title: 'Alustatalous' },
        { id: 'tek-6', title: 'Datatalous' }
      ]
    },
    {
      id: 'vaesto',
      title: 'Väestö',
      icon: '👥',
      color: SCENARIO_COLORS.Väestö,
      variables: [
        { id: 'vae-1', title: 'Syntyvyys' },
        { id: 'vae-2', title: 'Elinajanodote' },
        { id: 'vae-3', title: 'Maahanmuutto' },
        { id: 'vae-4', title: 'Ikärakenne' },
        { id: 'vae-5', title: 'Alueellinen jakautuminen' },
        { id: 'vae-6', title: 'Työvoiman saatavuus' }
      ]
    }
  ];

  const pivotTemplates = [
    { text: 'Tekoäly nopeuttaa päätöksentekoa', connectedVariables: ['dem-4', 'tek-1', 'tek-3'], participant: 'Ryhmä A' },
    { text: 'Ilmastopakolaisuus kasvaa', connectedVariables: ['ilm-5', 'ilm-6', 'vae-3', 'dem-3'], participant: 'Ryhmä B' },
    { text: 'Automaatio korvaa työpaikkoja', connectedVariables: ['tek-2', 'tek-1', 'vae-6', 'dem-2'], participant: 'Ryhmä A' },
    { text: 'Sään ääri-ilmiöt lisäävät muuttoliikettä', connectedVariables: ['ilm-3', 'ilm-6', 'vae-5', 'dem-3'], participant: 'Ryhmä C' },
    { text: 'Digitaaliset alustat keskittävät valtaa', connectedVariables: ['tek-5', 'tek-6', 'dem-4', 'dem-5'], participant: 'Ryhmä B' },
    { text: 'Ikääntyvä väestö rasittaa taloutta', connectedVariables: ['vae-4', 'vae-2', 'dem-1'], participant: 'Ryhmä A' },
    { text: 'Vihreä siirtymä luo työpaikkoja', connectedVariables: ['ilm-1', 'dem-6', 'vae-6', 'tek-3'], participant: 'Ryhmä C' },
    { text: 'Syntyvyyden lasku uhkaa hyvinvointivaltiota', connectedVariables: ['vae-1', 'vae-4', 'dem-1'], participant: 'Ryhmä B' },
    { text: 'Kyberuhkat heikentävät demokratiaa', connectedVariables: ['tek-4', 'dem-4', 'dem-5'], participant: 'Ryhmä A' },
    { text: 'Maahanmuutto paikkaa työvoimapulaa', connectedVariables: ['vae-3', 'vae-6', 'dem-3'], participant: 'Ryhmä C' }
  ];

  const singleNodeTexts = [
    'Lämpötila nousee 2 astetta', 'EU panostaa puolustukseen', 'Kasvukausi pitenee',
    'Helleaallot yleistyvät', 'Tulvat lisääntyvät', 'Kuivuus uhkaa viljelyä',
    'Tekoäly tehostaa hallintoa', 'Disinformaatio lisääntyy', 'Kansalaistoiminta aktivoituu',
    'Nuoret muuttavat kaupunkeihin', 'Maaseutu autioituu', 'Etätyö yleistyy',
    'Koulutustaso nousee', 'Terveyserot kasvavat', 'Eriarvoisuus syvenee'
  ];

  const allVariables = demoScenarios.flatMap(s => s.variables.map(v => v.id));
  const demoNodes = [];

  pivotTemplates.forEach((template, i) => {
    demoNodes.push({ id: `pivot-${i}`, ...template });
  });

  singleNodeTexts.forEach((text, i) => {
    const randomVar = allVariables[Math.floor(Math.random() * allVariables.length)];
    demoNodes.push({
      id: `node-${i}`,
      text,
      connectedVariables: [randomVar],
      participant: ['Ryhmä A', 'Ryhmä B', 'Ryhmä C'][Math.floor(Math.random() * 3)]
    });
  });

  return { scenarios: demoScenarios, nodes: demoNodes };
}
